import { ActionContext } from "vuex";

import Api from "@/api/Api";
import Lang from "@/lang";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { RootState } from "@/store";
import { CatalogsState } from "@/store/modules/catalogs";
import { FilterState } from "@/store/modules/userSettings";
import { ItemLayout, WidgetConfig } from "@/types/layouts";

export interface TemplateConfig {
  id: string;
  name: string;
  scope: string;
  owner: string;
  created_at: string;
  updated_at: string;
}

export interface TemplateData {
  settingsVersion: string;
  filterState: FilterState;
  layout: ItemLayout[];
}

export interface TemplateDataApi {
  layout: Array<WidgetConfig>;
  filterState: FilterState;
}

export interface TemplateConfigApi {
  id: string;
  name: string;
  owner: string;
  payload: TemplateDataApi;
  updated_at: string | null;
  created_at: string | null;
}

export interface TemplateSettings {
  templates: TemplateConfig[];
}

interface ProjectsTemplates {
  projectsTemplates: TemplateConfig[];
  privateTemplates: TemplateConfig[];
  publicTemplates: TemplateConfig[];
  selectedTemplate: null | TemplateConfig;
  isLoading: boolean;
}

const templatesState: ProjectsTemplates = {
  projectsTemplates: [],
  privateTemplates: [],
  publicTemplates: [],
  selectedTemplate: null,
  isLoading: false,
};

export default {
  namespaced: true,

  state: templatesState,

  actions: {
    async fetchTemplate(ctx: ActionContext<ProjectsTemplates, RootState>, templateId: string): Promise<TemplateConfig> {
      ctx.commit("updateLoading", true);

      const template = await Api.request<TemplateConfig>({
        path: `${API_ROUTES.PLAN_TEMPLATES.PATH}/${templateId}`,
        token: requestTokens.apiGetTemplates,
        defaultResult: {} as TemplateConfig,
        errorText: Lang.errorGetTemplates,
      });

      ctx.commit("updateLoading", false);
      return template;
    },

    async fetchTemplates(ctx: ActionContext<ProjectsTemplates, RootState>): Promise<void> {
      const res = await Api.request<TemplateConfig[] | undefined>({
        path: `${API_ROUTES.PLAN_TEMPLATES.PATH}`,
        token: requestTokens.apiGetTemplates,
        defaultResult: undefined,
        errorText: Lang.errorGetTemplates,
      });

      if (res !== undefined) {
        const templates: TemplateConfig[] = res ?? [];

        ctx.commit("setProjectsTemplates", templates);

        const privateTemplates = templates.filter((t) => t.scope === "private");
        const publicTemplates = templates.filter((t) => t.scope === "public");

        ctx.commit("setPrivateTemplates", privateTemplates);
        ctx.commit("setPublicTemplates", publicTemplates);
      }
    },

    async deleteTemplate(ctx: ActionContext<ProjectsTemplates, RootState>, template: TemplateConfig): Promise<void> {
      await Api.request({
        path: `${API_ROUTES.PLAN_TEMPLATES.PATH}/${template.id}`,
        method: "del",
        defaultResult: undefined,
        errorText: Lang.errorDeleteTemplate,
        token: requestTokens.apiDelTemplate,
      });
    },

    async publishTemplate(ctx: ActionContext<ProjectsTemplates, RootState>, templateId: string): Promise<void> {
      await Api.request({
        path: `${API_ROUTES.PLAN_TEMPLATES.PATH}/${templateId}/publish`,
        method: "post",
        defaultResult: "",
        token: requestTokens.apiPublishTemplate,
      });
    },
  },

  mutations: {
    updateLoading(state: CatalogsState, val: boolean): void {
      state.isLoading = val;
    },

    updateSelectedTemplate(state: ProjectsTemplates, item: TemplateConfig): void {
      state.selectedTemplate = item;
    },
    setProjectsTemplates(state: ProjectsTemplates, items: TemplateConfig[]): void {
      state.projectsTemplates = items;
    },
    setPrivateTemplates(state: ProjectsTemplates, items: TemplateConfig[]): void {
      state.privateTemplates = items;
    },
    setPublicTemplates(state: ProjectsTemplates, items: TemplateConfig[]): void {
      state.publicTemplates = items;
    },

    resetSelectedTemplate(state: ProjectsTemplates): void {
      state.selectedTemplate = null;
    },
  },

  getters: {
    listTemplates: (state: ProjectsTemplates): TemplateConfig[] => {
      return state.projectsTemplates;
    },

    privateTemplates: (state: ProjectsTemplates): TemplateConfig[] => {
      return state.privateTemplates;
    },
    publicTemplates: (state: ProjectsTemplates): TemplateConfig[] => {
      return state.publicTemplates;
    },

    selectedTemplate: (state: ProjectsTemplates): TemplateConfig | null => {
      return state.selectedTemplate;
    },

    isLoading(state: CatalogsState): boolean {
      return state.isLoading;
    },
  },
};
